import { useEffect, useRef } from 'react';
import Image from 'next/image';
import { cx, getDownloadURL } from '@/utils';
import SvgArrow from '@common/Icons/Arrow';
import { t } from '@lingui/macro';
import { TitleWrapper } from '@views/Home/components';
import { useHomeContext } from '@views/Home/HomeProvider/HomeContext';
import useEmblaCarousel from 'embla-carousel-react';

import {
  PageBlockStyles,
  PageSocialNetworksBlock,
} from '@/components/common/Providers/GraphqlProvider/generated';
import { splitTitle } from '@/components/views/Home/components/TitleWrapper';

import NewsSocialMedia from '../../PageNewsletterBlock/public2/components/NewsSocialMedia';
import styles from './styles.module.css';

interface BlockStyle {
  isA: boolean;
  isB: boolean;
  isC: boolean;
}
const FollowUs = ({
  currentPageSocialNetworks,
}: {
  currentPageSocialNetworks?: PageSocialNetworksBlock;
}) => {
  const {
    home: { socialNetworks: socialNetworksContext },
  } = useHomeContext();

  const socialNetworks = currentPageSocialNetworks || socialNetworksContext;

  const blockStyle: BlockStyle = {
    isA:
      !socialNetworks ||
      !socialNetworks.pageBlockStyle ||
      socialNetworks.pageBlockStyle === PageBlockStyles.A,
    isB: socialNetworks?.pageBlockStyle === PageBlockStyles.B,
    isC: socialNetworks?.pageBlockStyle === PageBlockStyles.C,
  };

  if (socialNetworks?.isDisabled) return null;

  const [title1, title2] = splitTitle(socialNetworks?.blockTitle, {
    show: socialNetworks?.showTitle,
    defaultTitle1: '',
    defaultTitle2: '',
  });

  return (
    <section className={'inner-extend relative z-content my-10'}>
      <TitleWrapper
        title1={title1}
        title2={title2}
        description={socialNetworks?.showDescription ? socialNetworks.publicDescription : ''}
      />
      <div className={cx(styles.wrapper)}>
        {blockStyle.isA && <VariantA socialNetworks={socialNetworks} blockStyle={blockStyle} />}
        {blockStyle.isB && <VariantB socialNetworks={socialNetworks} blockStyle={blockStyle} />}
      </div>
    </section>
  );
};

export default FollowUs;

export function FollowUsSkeleton({
  currentPageSocialNetworks,
}: {
  currentPageSocialNetworks?: PageSocialNetworksBlock;
}) {
  const socialNetworks = currentPageSocialNetworks;

  const blockStyle: BlockStyle = {
    isA:
      !socialNetworks ||
      !socialNetworks.pageBlockStyle ||
      socialNetworks.pageBlockStyle === PageBlockStyles.A,
    isB: socialNetworks?.pageBlockStyle === PageBlockStyles.B,
    isC: socialNetworks?.pageBlockStyle === PageBlockStyles.C,
  };

  return (
    <section className="inner-extend my-10">
      <div className="flex h-full animate-pulse flex-col justify-center">
        {blockStyle.isA && (
          <div className="flex h-full items-center gap-4">
            <div className="h-[540px] w-1/2 rounded bg-gray-200" />
            <div className="flex w-1/2 flex-col gap-3">
              <div className="h-6 w-full rounded bg-gray-200" />
              <div className="h-6 w-full rounded bg-gray-200" />
              <div className="h-6 w-3/4 rounded bg-gray-200" />
            </div>
          </div>
        )}
        {(blockStyle.isB || blockStyle.isC) && (
          <div className="flex h-full items-center justify-center">
            <div className="flex gap-4">
              <div className="h-36 w-36 rounded bg-gray-200" />
              <div className="h-36 w-36 rounded bg-gray-200" />
              <div className="h-36 w-36 rounded bg-gray-200" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const VariantA = ({
  socialNetworks,
  blockStyle,
}: {
  socialNetworks?: PageSocialNetworksBlock;
  blockStyle: BlockStyle;
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const divRef = useRef(null);
  useEffect(() => {
    const handleResize = () => {
      getAndSetHeightById('social_network_div', 'social_network_carousel');
    };

    const observer = new ResizeObserver(handleResize);

    if (divRef.current) {
      observer.observe(divRef.current);
    }

    return () => {
      if (divRef.current) {
        observer.unobserve(divRef.current);
      }
    };
  }, []);

  return (
    <>
      <div className="hidden w-full flex-[1_0_0] items-center gap-[16px] rounded-[10px] border-2 border-solid border-blueLight bg-primary p-[0px_8px] lg:flex lg:w-1/2">
        <div>
          <button
            className={'inset-y-0 -left-5 z-base hidden rotate-180 md:block'}
            onClick={() => emblaApi?.scrollPrev()}
          >
            <SvgArrow className="text-white" />
          </button>
        </div>
        <div id="social_network_carousel" className="h-full flex-1 overflow-hidden" ref={emblaRef}>
          <div className={'flex h-full gap-[32px]'}>
            {[...(socialNetworks?.leftItems ?? [])]
              .sort((a: any, b: any) => a.orderIndex - b.orderIndex)
              .map(
                (item, key) =>
                  item.image && (
                    <div
                      className={cx('relative h-full w-6/12 min-w-0 flex-[0_0_100%] ')}
                      key={key}
                    >
                      <Image
                        src={getDownloadURL(item.image.id)}
                        alt={'follow'}
                        fill
                        sizes="(min-width: 768px) 31.733vw, (min-width: 420px) 50vw, 100vw"
                        className="object-cover"
                      />
                    </div>
                  )
              )}
          </div>
        </div>
        <div>
          <button
            className={'inset-y-0 -right-5 z-base hidden md:block'}
            onClick={() => emblaApi?.scrollNext()}
          >
            <SvgArrow className="text-white" />
          </button>
        </div>
      </div>
      <div id="social_network_div" className="w-full lg:w-1/2" ref={divRef}>
        {socialNetworks &&
          (socialNetworks?.rightItems?.length || 0) > 0 &&
          socialNetworks.pageBlockStyle && (
            <NewsSocialMedia
              items={socialNetworks.rightItems.map((e) => ({
                id: e.id,
                link: e.linkItem?.url,
                snsSocialNetworkType: e.socialNetworkType,
                orderIndex: e.orderIndex,
              }))}
              blockStyle={blockStyle}
            />
          )}
      </div>
    </>
  );
};
const VariantB = ({
  socialNetworks,
  blockStyle,
}: {
  socialNetworks?: PageSocialNetworksBlock;
  blockStyle: any;
}) => {
  const divRef = useRef(null);
  return (
    <div id="social_network_div" className="w-full justify-center lg:w-1/2" ref={divRef}>
      {socialNetworks &&
        (socialNetworks?.rightItems?.length || 0) > 0 &&
        socialNetworks.pageBlockStyle && (
          <NewsSocialMedia
            items={socialNetworks.rightItems.map((e) => ({
              id: e.id,
              link: e.linkItem?.url,
              snsSocialNetworkType: e.socialNetworkType,
              orderIndex: e.orderIndex,
            }))}
            blockStyle={blockStyle}
          />
        )}
    </div>
  );
};

function getAndSetHeightById(sourceId: string, targetId: string) {
  const sourceElement = document.getElementById(sourceId);
  const targetElement = document.getElementById(targetId);
  if (sourceElement && targetElement) {
    const height = sourceElement.clientHeight;
    targetElement.style.height = height + 'px';
    return height;
  }
  return 0;
}
